import BaseCard from 'Prototype/Level/Card/BaseCard';
import { CardModifierType } from 'Prototype/Level/Card/CardModifierType';
import GameObject, { GameObjectEvent } from 'GameObject/GameObject';
import System from '../System';
import Container from 'Prototype/Container';
import Point from 'Utility/Point';
import { CardTypeMap } from 'Prototype/Level/Card/CardTypeMap';
import { CardActions } from 'State/Card/CardActions';
import CardComponent from 'Component/Level/Card';
import Renderable from 'Component/Renderable';
import Animator from 'Prototype/Animator';
import LevelIntroTask from './Task/LevelIntroTask';
import { LevelStatus } from 'State/Level/LevelStatus';
import CardStateSyncTask from './Task/CardStateSyncTask';
import BonusCardsTask from './Task/BonusCardsTask';
import LevelCompleteTask from './Task/LevelCompleteTask';
import CardStateHelper from 'State/Card/CardStateHelper';
import { delay } from 'Utility/Task/TaskQueue';
import { Anim, StartAnimationClip } from '../Animation/AnimationHelper';
import LevelCompleteMenuTask from './Task/LevelCompleteMenuTask';
import Game from 'Game';
import { Leaderboard } from '../UI/LeaderboardHelper';
import Sprite from 'Prototype/Sprite';
import { AUDIO_PACKS } from '../Audio';
import { CARD_STACK_OFFSET } from 'Constants/InGame';
import TransformComponent from 'Component/Transform';
import Interactable from 'Component/Interactable';
import { GBCHandler } from 'Utility/SDK/GBCHandler';
import { ResourceDriver } from 'Utility/Resource/ResourceManager';
import { IGameState } from 'State/Game/IGameState';
import { ICardsState } from 'State/Card/ICardState';
import { report } from 'report';
import ValueCard from 'Prototype/Level/Card/ValueCard';
import { UIEvent } from '../UI/BaseUISystem';
import { PlayInitialCardTask } from './Task/PlayInitialCardTask';
import { RestoreInteractivityTask } from './Task/RestoreInteractivityTask';
import { isGoldLevel } from 'State/Level/LevelStateHelper';
import { FTUXType } from 'Player/PlayerProperty/Progress/FTUXType';
import GoalMenuTask from '../Level/Task/GoalMenuTask';
import { sleep, timeoutPromise } from 'Utility/async';
import BoosterHUDHandler from '../UI/HUD/BoosterHUDHandler';
import { ProgressStateHelper } from 'State/Player/Progress/ProgressStateHelper';
import StreakBar from 'Prototype/UI/Level/StreakBar';
import { STREAK_PROGRESS_STEPS } from 'Constants/Level';
import { FTUXStateHelper } from 'State/Game/FTUX/FTUXStateHelper';
import { PlayAnyBoosterTask } from './Task/PlayAnyBoosterTask';
import { CardStack } from './CardStack';
import { LevelHud } from 'System/UI/HUD/LevelHud';
import InitModifiersTask from './Task/InitModifiersTask';
import Reasons from 'Utility/Reasons';
import { Pulse } from 'System/Animation/Clip';
import { RevealPlinkoCardTask } from './Task/RevealPlinkoCardTask';
import { WorldDataHelper } from 'Utility/WorldDataHelper';

export function getCardHandler(card: BaseCard) {
    const cardComponent = card.getComponent(CardComponent);
    return new CardTypeMap[cardComponent.type].handler(card);
}

export enum CardEvent {
    CARD_PLAYED = 'card_played',
}

export type DisableCardsInputReason = 'matchConnectedCardAnimationPlaying';

export default class CardSystem extends System {
    public systemContainer: Container;
    public cardsContainer: Container;
    public stackOuterContainer: Container;
    public stackContainer: Container;
    public animator: Animator;
    public cards: BaseCard[];
    public currentLevelState: LevelStatus;
    public cardStack: CardStack;
    public disableCardsInputReasons: Reasons<DisableCardsInputReason> = new Reasons();
    private cardMatchHintRoutine: any;
    private _levelActiveHandled: boolean;

    public onStart(): void {
        this.addContainers();
        this.cards = [];

        this.cardStack = new CardStack({ getCardsInStack: () => this.getCardsInStack(), system: this });
        this.stackOuterContainer.addChild(this.cardStack.labelLayout.root);
        this.stackOuterContainer.addChild(this.cardStack.dangerGlowLayout.root);
    }

    public async preload(): Promise<any> {
        // Load all required card audios from audio sprite sheet.
        // please take a look at README on resources/audio directory.
        Game.resource.add(AUDIO_PACKS.cardSystem, undefined, ResourceDriver.SECONDARY);
    }

    private addContainers() {
        this.systemContainer = GameObject.Instantiate<Container>(Container, {
            name: 'cardSystemContainer',
        });
        this.cardsContainer = GameObject.Instantiate<Container>(Container, {
            name: 'cardsContainer',
            relativePosition: new Point(0.5, -0.03),
            scale: new Point(1, 1),
        });
        this.stackOuterContainer = GameObject.Instantiate<Container>(Container, {
            name: 'stackOuterContainer',
            relativePosition: new Point(0.5, 1),
            scale: new Point(1, 1),
        });
        this.stackContainer = GameObject.Instantiate<Container>(Container, {
            name: 'stackContainer',
            position: new Point(Math.abs(CARD_STACK_OFFSET / 2), -90),
        });

        const stackBackground = GameObject.Instantiate(Sprite, {
            name: 'deck_holder',
            texture: 'deck_holder',
            anchor: new Point(0.5, 1),
            scale: new Point(1, 1),
            relativePosition: new Point(0, 0),
        });

        this.stackOuterContainer.addChild(stackBackground);
        stackBackground.addChild(this.stackContainer);

        this.animator = GameObject.Instantiate(Animator);

        this.systemContainer.addChild(this.cardsContainer);
        this.systemContainer.addChild(this.stackOuterContainer);
        this.systemContainer.addChild(this.animator);

        this.scene.addGameObject(this.systemContainer);
    }

    public handleStateChanged(state: IGameState) {
        // Always make sure we have the necessary game objects to draw the new state.
        this.createNewCards();

        switch (state.levelState.status) {
            case LevelStatus.LOADING:
                this.currentLevelState = LevelStatus.LOADING;
                //
                break;
            case LevelStatus.INTRO:
                if (this.currentLevelState !== LevelStatus.INTRO) {
                    this.currentLevelState = LevelStatus.INTRO;

                    //this.taskQueue.queue(new ResetCardState());
                    this.taskQueue.queue(new InitModifiersTask());
                    this.taskQueue.queue(new LevelIntroTask());
                    if (WorldDataHelper.isPlinkoLevel(state.levelState.levelNumber)) {
                        this.taskQueue.queue(new RevealPlinkoCardTask());
                    }
                    this.startTasks();
                }

                break;
            case LevelStatus.ACTIVE:
                const prevStatus = this.currentLevelState;
                this.currentLevelState = LevelStatus.ACTIVE;

                if (prevStatus !== LevelStatus.ACTIVE) {
                    this.handleLevelReady(state);
                } else {
                    this.restartHintTimer();
                    this.taskQueue.queue(new CardStateSyncTask());
                }

                this.startTasks();

                break;
            case LevelStatus.COMPLETE:
                if (this.currentLevelState !== LevelStatus.COMPLETE) {
                    this.currentLevelState = LevelStatus.COMPLETE;

                    this.stopCoroutine(this.cardMatchHintRoutine);

                    // Sync last task.
                    this.taskQueue.queue(new CardStateSyncTask());
                    this.startTasks();
                    this.handleLevelComplete(state);
                }
                break;
        }

        this.cardStack.timerSelector(state);
    }

    private createNewCards(): void {
        const state = this.store.getState();

        if (!state || !state.levelState.cardsState) return;

        // Get an array of all card gameobject IDs to compare with IDs in the new state.
        const gameObjectIDs = this.cards.map((card: BaseCard) => {
            return card.id;
        });
        for (const cardID in state.levelState.cardsState.cards) {
            if (
                Object.prototype.hasOwnProperty.call(state.levelState.cardsState.cards, cardID) &&
                gameObjectIDs.indexOf(cardID) === -1
            ) {
                const cardData = CardStateHelper.getCardStateData(state, cardID);
                const cardState = CardStateHelper.getCardState(state, cardID);
                const newCard: BaseCard = GameObject.Instantiate(CardTypeMap[cardData.type].prototype, {
                    ...cardData,
                    currentState: cardState,
                    cardBackTexture: Game.player.getCardBackTexture(),
                });

                this.cards.push(newCard);

                newCard.on(GameObjectEvent.POINTER_DOWN, () => {
                    if (this.disableCardsInputReasons.hasAny()) return;

                    if (!this.taskQueue.isRunning() || this.taskQueue.getCurrentTask() instanceof CardStateSyncTask) {
                        this.playCard(cardData.id);
                    }
                });
            }
        }
    }

    private async handleLevelComplete(state: IGameState): Promise<void> {
        // This promise is sequentially handling any existing tasks and then checking updating the Streakbar promise.
        // This flow here is only mimicking existing functionality. Unknown if it must remain this way. Now the tasks
        // and the Streakbar promise both must complete before the 5000ms sleep - otherwise they will be ignored.
        const waitForPromise = async () => {
            await this.awaitAllTasks();

            // TODO: It is unclear why this streak logic is here. Documentation required.
            if (
                state.levelState.cardsState.matchSequence.length > 0 &&
                state.levelState.cardsState.matchSequence.length % STREAK_PROGRESS_STEPS === 0
            ) {
                // TODO: This can cause issues due to the subscribers never being notified after the level completes.
                // StreakBar.update() is not called if the last played card is a watermelon card - unclear why. This is likely
                // causing rare "freezing" issue where game does not progress to "BonusCardsTask".
                await StreakBar.instance.updatePromise(state.levelState.cardsState.turns);
                await sleep(1000);
            }
        };

        // Race to complete tasks, skip after 5000ms.
        // TODO: This should be refactored as timeouts/races are only bandage fixes.
        const hanged = !(await timeoutPromise(5000, waitForPromise()));

        if (hanged) {
            report('Awaiting tasks took more than specified timeout', {
                once: true,
                sampleRate: 0.1,
            });
        }

        await this.disableCardsInputReasons.untilHas('matchConnectedCardAnimationPlaying');

        // Level completed sequence.
        this.taskQueue.queue(new BonusCardsTask());
        this.taskQueue.queue(new LevelCompleteTask());
        this.taskQueue.queue(new LevelCompleteMenuTask());
        this.startTasks();

        // Post scores. All leaderboards take the highest reported value.

        // Score earned for completing this level.
        GBCHandler.updateLeaderboard(
            Leaderboard.LEVEL_SCORE,
            state.levelState.levelNumber.toString(),
            state.levelState.score
        );

        // Number of stars earned in the current world.
        GBCHandler.updateLeaderboard(
            Leaderboard.WORLD_STARS,
            state.worldState.worldNumber.toString(),
            ProgressStateHelper.getWorldStarsTotal(state.playerState, state.worldState.worldNumber)
        );

        // Number of total stars earned.
        GBCHandler.updateLeaderboard(
            Leaderboard.TOTAL_STARS,
            'default',
            ProgressStateHelper.getStarsCount(state.playerState)
        );

        // The total levels completed.
        GBCHandler.updateLeaderboard(Leaderboard.TOTAL_LEVELS, 'default', state.levelState.levelNumber);
    }

    private async handleLevelReady(state: IGameState): Promise<void> {
        this.taskQueue.queue(new CardStateSyncTask());
        this.startTasks();

        await sleep(200);

        await BoosterHUDHandler.tutorialPlayingDeferred?.read.promise;

        await this.awaitAllTasks();

        if (isGoldLevel(state.levelState) && !FTUXStateHelper.isFtuxCompleted(state.playerState, FTUXType.GOLD_CARD)) {
            this.taskQueue.queue(new GoalMenuTask());
            this.startTasks();

            await this.awaitAllTasks();
            this.scene.emit(UIEvent.DISABLE_INTERACTIVITY);
            await sleep(500);
        }

        await this.usePreBoosters(state);

        this.taskQueue.queue(new PlayInitialCardTask());
        this.taskQueue.queue(new RestoreInteractivityTask());
        this.startTasks();
        await this.awaitAllTasks();
        this.restartHintTimer();

        this._levelActiveHandled = true;
    }

    private async usePreBoosters(state: IGameState): Promise<void> {
        for (const preBooster of state.levelState.preBoosters) {
            this.taskQueue.queue(new PlayAnyBoosterTask(preBooster));
            this.startTasks();
            await this.awaitAllTasks();
        }
    }

    private restartHintTimer(): void {
        this.stopCoroutine(this.cardMatchHintRoutine);

        const state = this.store.getState();

        // Do not show hint animation if there is an imminent fail-state e.g.
        // The TNT modifier is about to explode. We do not want to hint players
        // to play cards that would cause them to fail the level.
        if (CardStateHelper.failStateIsImminent(state)) return;

        this.cardMatchHintRoutine = this.startCoroutine(this.cardMatchHint(state));
    }

    private async awaitAllTasks(): Promise<void> {
        while (this.taskQueue.isRunning()) {
            await sleep(1);
        }
    }

    public async startTasks(): Promise<void> {
        this.taskQueue.start(this);
        await this.awaitAllTasks();
        this.scene.emit(UIEvent.DISABLE_INTERACTIVITY);
        this.scene.emit(UIEvent.RESTORE_INTERACTIVITY);
    }

    public playCard(cardID: string): void {
        CardActions.playCard(cardID);
        this.scene.emit(CardEvent.CARD_PLAYED, cardID);
    }

    private *cardMatchHint(state: IGameState): IterableIterator<number> {
        let changeOrder: boolean = true;
        while (true) {
            yield* delay(5000);

            const cardsFolded = this.getCardsFolded();
            const topCardFolded = cardsFolded[cardsFolded.length - 1];
            if (topCardFolded === undefined) return;

            const matchedCards = CardStateHelper.getCardMatches(state)
                .map(id => this.getCardByID(id))
                .sort((a, b) => Number(b instanceof ValueCard) - Number(a instanceof ValueCard));

            if (matchedCards.length > 0) {
                this.playHintPulse(matchedCards[0]);
            } else {
                if (state.levelState.cardsState.matchSequence.length % STREAK_PROGRESS_STEPS >= 3) {
                    if (changeOrder) {
                        this.JokerMatchHint();
                        changeOrder = !changeOrder;
                    } else {
                        this.DeckMatchHint();
                        changeOrder = !changeOrder;
                    }
                } else {
                    this.DeckMatchHint();
                }
            }
        }
    }

    private JokerMatchHint() {
        const sc = [1.1, 0.9, 1.1, 0.9, 1.1, 0.9, 1];
        Anim(StreakBar.instance.reward, { scaleX: sc, scaleY: sc, duration: 1400 });
        Anim(LevelHud.instance.getLayout().jokerButton, { scaleX: sc, scaleY: sc, duration: 1400 });
    }

    private DeckMatchHint() {
        const cardsInStack = this.getCardsInStack();
        const topCardInStack = cardsInStack[cardsInStack.length - 1];
        this.playHintPulse(topCardInStack);
    }

    private playHintPulse(card: BaseCard) {
        if (card === undefined) return;

        StartAnimationClip(new Pulse(card, {}));
    }

    public sortCardsInPlay(state: ICardsState) {
        state.inPlay
            .map(cardID => state.cards[cardID])
            .sort((a, b) => a.depth - b.depth)
            .forEach(cardID => {
                const card = this.getCardByID(cardID.id);
                this.cardsContainer.addChild(card);
            });
    }

    public hasCardsInPlay(): boolean {
        return this.getCardsInPlay().length > 0;
    }

    public getCardsInPlay(): BaseCard[] {
        try {
            const cardsInPlay = this.store.getState().levelState.cardsState.inPlay;
            return cardsInPlay.map((cardID: string) => {
                return this.getCardByID(cardID);
            }) as BaseCard[];
        } catch (error) {
            report('Error in getting cards in play', {
                error,
                once: true,
                sampleRate: 0.1,
            });
            return [];
        }
    }

    public getCardsInStack(): BaseCard[] {
        try {
            const cardsInStack = this.store.getState().levelState.cardsState.inStack;
            return cardsInStack
                .map((cardID: string) => {
                    return this.getCardByID(cardID);
                })
                .filter(Boolean) as BaseCard[];
        } catch (error) {
            report('Error in getting cards in stack', {
                error,
                once: true,
                sampleRate: 0.1,
            });
            return [];
        }
    }

    public getCardsFolded(): BaseCard[] {
        try {
            const cardsFolded = this.store.getState().levelState.cardsState.folded;
            return cardsFolded.map((cardID: string) => {
                return this.getCardByID(cardID);
            }) as BaseCard[];
        } catch (error) {
            report('Error in getting cards folded', {
                error,
                once: true,
                sampleRate: 0.1,
            });
            return [];
        }
    }

    public getCardByID(cardID: string): BaseCard {
        const card = this.cards.find(gameObject => gameObject.id === cardID);

        if (!card) {
            const state = this.store.getState();

            report('Card not found', {
                once: true,
                sampleRate: 0.1,
                tags: {
                    cardID,
                    levelNumber: state.levelState.levelNumber,
                },
                fingerprint: 'getCardByID:CardNotFound',
                state,
            });
        }

        return card;
    }

    public hasCardInPlayWithModifier(modifierType: CardModifierType): boolean {
        try {
            const cardsState = Game.store.getState().levelState.cardsState;
            return this.getCardsInPlay().some(card =>
                cardsState.cards[card.id].modifiers.some(modifierData => modifierData.type === modifierType)
            );
        } catch (error) {
            report('hasCardInPlayWithModifier crashed', {
                error,
                once: true,
                sampleRate: 0.1,
            });
            return false;
        }
    }

    public getAllCards(): BaseCard[] {
        try {
            return [
                ...this.store.getState().levelState.cardsState.inPlay,
                ...this.store.getState().levelState.cardsState.discarded,
                ...this.store.getState().levelState.cardsState.inStack,
                ...this.store.getState().levelState.cardsState.folded,
            ].map((cardID: string) => {
                return this.getCardByID(cardID);
            }) as BaseCard[];
        } catch (error) {
            report('Error in getting all cards', {
                error,
                once: true,
                sampleRate: 0.1,
            });
            return [];
        }
    }

    public init(): void { }

    protected onDestroy(): void { }

    protected onStop(): void { }

    protected onUpdate(): void { }

    public onLateUpdate(): void {
        if (this.currentLevelState === LevelStatus.INTRO || this.currentLevelState === LevelStatus.ACTIVE) {
            this.updateStackCardVisibility();
        }
    }

    public updateStackCardVisibility() {
        const cardsInStack = this.getCardsInStack();

        for (let i = 0, n = cardsInStack.length; i < n; i++) {
            cardsInStack[i].getComponent(Interactable).enabled = false;
        }

        if (cardsInStack.length !== 0) {
            cardsInStack[cardsInStack.length - 1].getComponent(Interactable).enabled = true;
        }

        const cardsFolded = this.getCardsFolded();

        for (let i = 0, n = cardsFolded.length; i < n; i++) {
            if (this.cardIsPosition(cardsFolded[i], 0, 0)) {
                if (this.cardIsPosition(cardsFolded[i + 1], 0, 0)) {
                    cardsFolded[i].getComponent(Renderable).visible = false;
                } else {
                    cardsFolded[i].getComponent(Renderable).visible = true;
                }
            }
        }
    }

    private cardIsPosition(card: BaseCard, x: number, y: number): boolean {
        if (!card) return false;
        const transform = card.getComponent(TransformComponent);
        return (
            transform.position.x === x &&
            //transform.position.y === y &&
            //transform.rotation === 0 &&
            card.parent === this.stackContainer
        );
    }

    public get LevelActiveHandled(): boolean {
        return this._levelActiveHandled;
    }
}
